import { action, computed, observable } from "mobx";
import { message } from "antd";
import { LoginResponse } from "../services/responses";
import ee from "../services/EventEmitter";
import { USER_HAS_AUTHORIZED, USER_LOADED } from "../constants/events";
import { STATUS_VALIDATION_ERROR } from "../constants/http";
import { addMessage } from "../helpers/flashMessage";
import { HOMEPAGE_PATH } from "../constants/auth";
import { IAuthorize } from "../components/Authorize/Iterfaces";
import {
  Permission,
  PermissionType,
  Role,
  RoleType,
} from "../constants/authorize";
import ApiManager from "../mixins/ApiManager";
import { User } from "../models/Users/User";

interface IErrors {
  email?: string[] | null;
  password?: string[] | null;
}

export default class UserStore extends ApiManager {
  @observable
  isSuccessRequest = true;

  @observable
  user: User | null = null;

  @observable
  private _errors: IErrors = {};

  @computed
  get errors(): IErrors {
    return this._errors;
  }

  @observable
  private _resetErrors: IErrors = {};

  @computed
  get resetErrors(): IErrors {
    return this._resetErrors;
  }

  constructor() {
    super();
    this.getAndSetCurrentUser();
  }

  @action
  login(login: string, password: string): Promise<Response> {
    this._errors = {};

    return this.apiManager
      .login(login, password)
      .then(async (response: LoginResponse) => {
        if (response.user) {
          this.user = response.user;
          this.updatedUserSettings();
        }

        this._errors = {};
        this.isSuccessRequest = true;
        ee.emit(USER_HAS_AUTHORIZED, null);

        return response;
      })
      .catch(async (response) => {
        const data = await response.json();

        if (response.status === STATUS_VALIDATION_ERROR) {
          this._errors = data.errors;
          message.error(data.message);
          return response;
        }

        message.error(data.message);
        this.isSuccessRequest = false;

        return response;
      });
  }

  @action
  logout(): Promise<Response> {
    this.user = null;
    return this.apiManager
      .logout()
      .then(async (response: Response) => {
        const data = await response.json();

        if (response.ok) {
          addMessage(data.message);
        } else {
          addMessage(data.message, "error");
        }

        return response;
      })
      .finally(() => {
        window.location.href = HOMEPAGE_PATH;
      });
  }

  @action
  sendResetPasswordRequest(parameters: Object): Promise<Response> {
    this._resetErrors = {};

    return this.apiManager
      .sendResetPasswordRequest(parameters)
      .then(async (response) => {
        const data = await response.json();

        this._resetErrors = {};

        message.success(data.message);
      })
      .catch(async (response) => {
        const data = await response.json();

        if (response.status === STATUS_VALIDATION_ERROR) {
          this._resetErrors = data.errors;
        }

        message.error(data.message);

        return response;
      });
  }

  private getAndSetCurrentUser(): void {
    const promise = this.apiManager.getCurrentUser();

    if (promise) {
      promise.then(async (response: Response) => {
        this.user = (await response.json()) as User;

        this.updatedUserSettings();
        ee.emit(USER_HAS_AUTHORIZED, null);
      });
    }
  }

  private updatedUserSettings(): void {
    ee.emit(USER_LOADED, this.user);
  }

  getCurrentUser(): User | null {
    return this.user;
  }

  authorize(permission?: PermissionType, role?: RoleType): boolean {
    let authorized = true;
    if (permission) authorized = this.authorizeByPermission(permission);
    if (role) authorized = authorized && this.authorizeByRole(role);

    return authorized;
  }

  authorizeByPermission(permission: PermissionType): boolean {
    if (Array.isArray(permission)) {
      return permission.some((name) => this.hasPermission(name));
    }

    return this.hasPermission(permission);
  }

  private hasPermission(name: Permission): boolean {
    if (!this.user) {
      return false;
    }

    if (!Array.isArray(this.user.permissions)) {
      return false;
    }

    if (this.user.permissions.length === 0) {
      return false;
    }

    return this.user.permissions.findIndex((item) => item === name) >= 0;
  }

  authorizeByRole(role: RoleType): boolean {
    if (Array.isArray(role)) {
      return role.some((name) => this.hasRole(name));
    }

    return this.hasRole(role);
  }

  private hasRole(name: Role): boolean {
    if (!this.user) {
      return false;
    }

    if (!Array.isArray(this.user.roles)) {
      return false;
    }

    if (this.user.roles.length === 0) {
      return false;
    }

    return this.user.roles.findIndex((item) => item === name) >= 0;
  }

  authorizedOnly<T extends IAuthorize>(entities: T[]): T[] {
    return entities.filter((entity) =>
      this.authorize(entity.onlyOnPermission, entity.onlyOnRole)
    );
  }
}
